import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { StethoscopeIcon, HeartPulseIcon, FlaskConicalIcon, ScanLineIcon, PillIcon, MicIcon, SparklesIcon, PlusIcon, SaveIcon, AlertTriangleIcon, StampIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Avatar } from '../components/ui/Avatar';
import { getPatient } from '../data/patients';
import { getProvider } from '../data/providers';
const soapFields = [{
  key: 'S',
  label: 'Subjective',
  placeholder: 'Chief complaint, history of present illness, review of systems…',
  value: 'Follow-up for exertional chest tightness. Reports improvement on current regimen. No syncope, no orthopnoea. Adherent to medication.'
}, {
  key: 'O',
  label: 'Objective',
  placeholder: 'Examination findings, vitals…',
  value: 'Alert, comfortable at rest. HR 96 irregular, BP 148/92. Chest clear. S1 S2 + no added sounds. No pedal edema.'
}, {
  key: 'A',
  label: 'Assessment',
  placeholder: 'Provisional & final diagnosis…',
  value: 'NSTEMI — stabilised. Poorly controlled hypertension. T2DM with suboptimal glycaemic control.'
}, {
  key: 'P',
  label: 'Plan',
  placeholder: 'Orders, prescriptions, follow-up…',
  value: 'Continue dual antiplatelet. Uptitrate anti-hypertensive. HbA1c + lipid panel. Cardiac rehab referral. Review in 2 weeks.'
}];
export function OpdConsultation() {
  const patient = getPatient('p1')!;
  const provider = getProvider('dr-imran-sethi')!;
  const [notes, setNotes] = useState(() => Object.fromEntries(soapFields.map(f => [f.key, f.value])));
  return <div className="p-6" data-id="element-3424" data-mp="aamk16jfe35s">
      <PageHeader title="OPD Consultation Workspace" subtitle={`${provider.clinic} · SOAP note · ICD-10 coded · signed with verified PMDC provider identity`} action={<>
            <Button variant="outline" size="sm" icon={<MicIcon className="h-4 w-4" data-id="element-3427" data-mp="2fg211daadoqc" />} data-id="element-3426" data-mp="2fez8z2189g8u">Dictate</Button>
            <Button icon={<SaveIcon className="h-4 w-4" data-id="element-3429" data-mp="2beggd5rig65l" />} data-id="element-3428" data-mp="2bdx5ua46wk1l">Sign & save</Button>
          </>} data-id="element-3425" data-mp="ed16s1q7tn2c" />

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]" data-id="element-3430" data-mp="2p5lluohk7snu">
        {/* Note editor */}
        <div className="space-y-4" data-id="element-3431" data-mp="2kelmlckywyi6">
          {/* Patient strip */}
          <Card className="flex flex-wrap items-center gap-4 p-4" data-id="element-3432" data-mp="2kc8x8ecvrlv3">
            <Avatar name={patient.name} src={patient.photo} size="md" data-id="element-3433" data-mp="2z1wvt7tog6f4" />
            <div data-id="element-3434" data-mp="2u4ohjibngmj5">
              <p className="text-sm font-semibold text-slate-900" data-id="element-3435" data-mp="2u33mlrlfb1kr">{patient.name}</p>
              <p className="text-xs text-slate-500" data-id="element-3436" data-mp="38469g6k8sfwq">{patient.mrn} · {patient.age}{patient.sex[0]} · {patient.bloodGroup}</p>
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-1.5" data-id="element-3437" data-mp="335mi1qmvqbwz">
              <Badge tone="brand" data-id="element-3438" data-mp="1dqog22x5oots">{provider.name} · verified</Badge>
              {patient.allergies.map(a => <Badge key={a.substance} tone="danger" icon={<AlertTriangleIcon className="h-3 w-3" data-id="element-3440" data-mp="3keffd3xygunr" />} data-id="element-3439" data-mp="18sdai4dw90hd">{a.substance}</Badge>)}
            </div>
          </Card>

          {/* AI summary */}
          <Card className="border-brand-200 bg-brand-50/40 p-4" data-id="element-3441" data-mp="3pcgujds9g9ty">
            <div className="flex items-center gap-2 text-brand-700" data-id="element-3442" data-mp="3bksljjdb61h7">
              <SparklesIcon className="h-4 w-4" data-id="element-3443" data-mp="3fn738058qv6w" />
              <span className="text-sm font-semibold" data-id="element-3444" data-mp="3flvzg0bspnfs">Clinical Summary (AI-assisted)</span>
              <Badge tone="brand" className="ml-auto" data-id="element-3445" data-mp="31mwd4zbr9kms">Draft</Badge>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-slate-700" data-id="element-3446" data-mp="36nsep68mwup1">
              54F, known CAD/T2DM/HTN, admitted 3 days ago with NSTEMI, now clinically stable. Last HbA1c 8.4%.
              BP remains above target. High bleeding risk noted (anticoagulated). Consider glycaemic optimisation and cardiac rehab.
            </p>
          </Card>

          {/* SOAP */}
          {soapFields.map(f => <Card key={f.key} className="p-4" data-id="element-3447" data-mp="uvwvs31sa0iz">
              <div className="mb-2 flex items-center gap-2" data-id="element-3448" data-mp="66k56ki4cjkl">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-900 text-xs font-bold text-white" data-id="element-3449" data-mp="6620czhe4x12">{f.key}</span>
                <h3 className="text-sm font-semibold text-slate-900" data-id="element-3450" data-mp="3sds4p66cxw8p">{f.label}</h3>
              </div>
              <textarea value={notes[f.key]} onChange={e => setNotes(n => ({
            ...n,
            [f.key]: e.target.value
          }))} placeholder={f.placeholder} rows={3} className="w-full resize-none rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm leading-relaxed text-slate-700 outline-none focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-100" data-id="element-3451" data-mp="3nep8mb8cv0cv" />
            </Card>)}

          {/* Diagnosis coding */}
          <Card className="p-4" data-id="element-3452" data-mp="2zpcsssiivu1l">
            <CardHeader title="Diagnoses (ICD-10)" subtitle="Structured & research-ready" className="px-0 pt-0" action={<Button size="sm" variant="outline" icon={<PlusIcon className="h-4 w-4" data-id="element-3455" data-mp="1b5qr1eckmj17" />} data-id="element-3454" data-mp="2vob1qyyreoap">Add</Button>} data-id="element-3453" data-mp="2zolagjdxrst9" />
            <div className="mt-3 space-y-2" data-id="element-3456" data-mp="1b576rqzg7ik0">
              {[{
              code: 'I21.4',
              text: 'Non-ST elevation myocardial infarction',
              type: 'Final'
            }, {
              code: 'I10',
              text: 'Essential (primary) hypertension',
              type: 'Final'
            }, {
              code: 'E11.65',
              text: 'T2DM with hyperglycaemia',
              type: 'Final'
            }].map(d => <div key={d.code} className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2" data-id="element-3457" data-mp="rij1ckdz9r7t">
                  <span className="font-mono text-xs font-semibold text-brand-700" data-id="element-3458" data-mp="whcgeh8wxqa3">{d.code}</span>
                  <span className="flex-1 text-sm text-slate-700" data-id="element-3459" data-mp="11evunh5m2321">{d.text}</span>
                  <Badge tone="success" data-id="element-3460" data-mp="166nl7roro8s6">{d.type}</Badge>
                </div>)}
            </div>
          </Card>
        </div>

        {/* Orders sidebar */}
        <div className="space-y-4" data-id="element-3461" data-mp="dq35q4neovgx">
          <Card data-id="element-3462" data-mp="dp1jr54uqokf">
            <CardHeader title="Vitals" subtitle="Nursing · 14:20" icon={<HeartPulseIcon className="h-4 w-4" data-id="element-3464" data-mp="5ovhica6ol2k" />} data-id="element-3463" data-mp="doiaect9p8i0" />
            <div className="mt-2 grid grid-cols-2 gap-px bg-slate-100 rounded-b-xl overflow-hidden" data-id="element-3465" data-mp="5nkc6813b59c">
              {patient.vitals.slice(0, 4).map(v => <div key={v.label} className="bg-white p-3" data-id="element-3466" data-mp="ffgha31lfvn2">
                  <p className="text-base font-bold font-display text-slate-900 tabular-nums" data-id="element-3467" data-mp="ag3svdv2qgid">{v.value}<span className="ml-0.5 text-[10px] font-normal text-slate-400" data-id="element-3468" data-mp="ai6qaiyk3bx4">{v.unit}</span></p>
                  <p className="text-[11px] text-slate-500" data-id="element-3469" data-mp="3iy006eydtp57">{v.label}</p>
                </div>)}
            </div>
          </Card>

          <Card className="p-4" data-id="element-3470" data-mp="3sud19xw9b59z">
            <h3 className="mb-3 text-sm font-semibold text-slate-900" data-id="element-3471" data-mp="3svo1xouenwwv">Quick Orders</h3>
            <div className="grid grid-cols-2 gap-2" data-id="element-3472" data-mp="3nu8lka36aekt">
              {[{
              icon: FlaskConicalIcon,
              label: 'Lab'
            }, {
              icon: ScanLineIcon,
              label: 'Radiology'
            }, {
              icon: PillIcon,
              label: 'e-Prescribe'
            }, {
              icon: StethoscopeIcon,
              label: 'Referral'
            }].map(o => <button key={o.label} className="flex flex-col items-center gap-1.5 rounded-lg border border-slate-200 py-3 text-xs font-medium text-slate-700 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 transition-colors" data-id="element-3473" data-mp="2l4qtu4n1use6">
                  <o.icon className="h-5 w-5" data-id="element-3474" data-mp="2h1u6y1qh8a4f" />
                  {o.label}
                </button>)}
            </div>
          </Card>

          <Card className="overflow-hidden" data-id="element-3475" data-mp="34sr7yjgqhn0w">
            <div className="bg-slate-900 px-4 py-3 text-white" data-id="element-3476" data-mp="38s8i3q73v8ce"><div className="flex items-center gap-2" data-id="element-3477" data-mp="38u3477u456i2"><StampIcon className="h-4 w-4 text-brand-300" data-id="element-3478" data-mp="2v1deotfdljg7" /><h3 className="text-sm font-semibold" data-id="element-3479" data-mp="2v0bthw4nbgkr">Prescription signing identity</h3></div><p className="mt-1 text-xs text-slate-400" data-id="element-3480" data-mp="2zydisyso0c4o">Automatically applied when the prescription is signed</p></div>
            <div className="p-4" data-id="element-3481" data-mp="3nny6fuzrjxka"><div className="flex items-center gap-3" data-id="element-3482" data-mp="3nnele5etgt21"><Avatar name={provider.name} src={provider.photo} size="md" data-id="element-3483" data-mp="3sg7oypyjtvcj" /><div className="min-w-0" data-id="element-3484" data-mp="3shsj4egybc9v"><p className="text-sm font-semibold text-slate-900" data-id="element-3485" data-mp="1se89923oym53">{provider.name}</p><p className="text-xs text-slate-500" data-id="element-3486" data-mp="1sfjd11x4ztw7">{provider.qualifications}</p><p className="mt-1 text-xs font-medium text-brand-700" data-id="element-3487" data-mp="1nlf3qc0ef9ry">{provider.pmdcNumber} · valid to {provider.pmdcExpiry}</p></div></div><div className="mt-3 border-2 border-brand-700 px-2 py-2 text-center text-[10px] text-brand-900" data-id="element-3488" data-mp="1ilu5k3t1z5ga"><p className="font-bold uppercase tracking-wide" data-id="element-3489" data-mp="1dmqykdtiip5o">Creative Energy Hospital</p><p className="font-semibold" data-id="element-3490" data-mp="2bc2cezi523fk">{provider.stampLine}</p><p data-id="element-3491" data-mp="26dr4iubem2zz">{provider.clinic} · {provider.room}</p></div></div>
          </Card>

          <Card className="p-4" data-id="element-3492" data-mp="26boc63ms3xs3">
            <h3 className="mb-3 text-sm font-semibold text-slate-900" data-id="element-3493" data-mp="21dmy5x47monh">Active Orders</h3>
            <div className="space-y-2" data-id="element-3494" data-mp="1xdcexireq1qo">
              {[{
              t: 'HbA1c + Lipid panel',
              s: 'Lab · pending',
              tone: 'info' as const
            }, {
              t: 'Aspirin 75mg OD',
              s: 'Pharmacy · dispensed',
              tone: 'success' as const
            }, {
              t: 'Cardiac rehab',
              s: 'Referral · sent',
              tone: 'brand' as const
            }].map(o => <motion.div key={o.t} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="rounded-lg border border-slate-100 p-2.5" data-id="element-3495" data-mp="2xiwpanrfm3d2">
                  <p className="text-sm font-medium text-slate-800" data-id="element-3496" data-mp="36l5xh6z89kna">{o.t}</p>
                  <Badge tone={o.tone} className="mt-1" data-id="element-3497" data-mp="3bce9marxzk45">{o.s}</Badge>
                </motion.div>)}
            </div>
          </Card>
        </div>
      </div>
    </div>;
}